import PropTypes from 'prop-types'
import * as R from 'ramda'
import React, { useContext, useState } from 'react'
import { mapProps } from 'recompose'
import { Message, Segment } from 'semantic-ui-react'
import styled from 'styled-components'

import useFeatureToggle from 'hooks/useFeatureToggle'

import { IconStyled } from 'components/QuestionActions/QuestionAction'
import { ReorderButtons } from 'components/ReorderButtons'
import { TooltipLabel } from 'components/TooltipLabel'
import useModal from 'hooks/useModal'
import { createContext } from 'libs/createContext'
import { PrimaryQuestionInput } from 'organisms/PrimaryQuestionInput'
import { QuestionInput } from 'organisms/QuestionInput'
import SurveyQuestionVisibilityModal from 'organisms/SurveyQuestionVisibilityModal'
import { surveySettingContext } from 'pages/SurveySettings/context'
import { useFormikContext } from 'formik'

import QuestionConditionLogic from 'organisms/QuestionConditionalLogic'
import { QuestionType } from 'packages/question-types'
const omitProps = (keys) => mapProps((props) => R.omit(keys, props))

const UnstyledSegment = omitProps(['primary'])(Segment)
const Sidebar = styled(UnstyledSegment)`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 116px !important;
  max-width: 116px;
  text-align: center;
  background-color: ${({ primary }) => (primary ? '#f4f4f4' : '')} !important;
  font-weight: bold;
`

const QuestionNumber = styled.span`
  font-size: 30px;
`

const Content = styled(Segment)`
  padding-right: 40px !important;
  @media only screen and (min-width: 768px) {
    padding: 26px 80px 26px 60px !important;
  }
`

const ContentWrapper = styled.div`
  max-width: 450px;
  margin-left: auto;
  margin-right: auto;
  @media only screen and (min-width: 768px) {
    max-width: unset;
  }
`

/**
 *
 * @param {IconSetOption} selectedIcon
 * @returns
 */
function transformIconSet(selectedIcon) {
  return {
    positive: selectedIcon.choice_value_2,
    neutral: selectedIcon.choice_value_3,
    negative: selectedIcon.choice_value_4,
    1: selectedIcon.choice_value_1,
    2: selectedIcon.choice_value_2,
    3: selectedIcon.choice_value_3,
    4: selectedIcon.choice_value_4,
    5: selectedIcon.choice_value_5,
  }
}

/**
 * @typedef {object} IconSetOption
 * @property {number} id
 * @property {string} choice_value_1
 * @property {string} choice_value_2
 * @property {string} choice_value_3
 * @property {string} choice_value_4
 * @property {string} choice_value_5
 *
 * @param {number} iconSetId
 * @param {IconSetOption[]} iconSetOptions
 * @returns
 */
function getIconSetById(iconSetId, iconSetOptions) {
  if (!iconSetId) {
    return {}
  }
  const selectedIcon = iconSetOptions.find((option) => option.id === iconSetId)
  if (selectedIcon) {
    return transformIconSet(selectedIcon)
  } else {
    return {}
  }
}

const questionBuilderReducer = (state, action) => {
  if (action.type === 'SET_ICON_SET') {
    return {
      iconSetId: action.iconSet.id,
      iconSet: transformIconSet(action.iconSet),
    }
  }
  return state
}

export const {
  Provider: QuestionBuilderProvider,
  Consumer: QuestionBuilderConsumer,
  context: QuestionBuilderContext,
} = createContext({}, questionBuilderReducer)

const semanticValueGetter = (_, { value }) => value

QuestionBuilderCard.propTypes = {
  arrayHelpers: PropTypes.object,
  primary: PropTypes.bool,
  question: PropTypes.object,
  questionResponse: PropTypes.object,
  questions: PropTypes.array,
  setFieldValue: PropTypes.func,
  questionIndex: PropTypes.number.isRequired,
  canChangePrimaryMetric: PropTypes.bool,
  onRemove: PropTypes.func,
  removeVisibilityCondition: PropTypes.func,
  disableMoveDown: PropTypes.bool,
  disableMoveUp: PropTypes.bool,
  disableActionsAfterSave: PropTypes.bool,
  onMoveDown: PropTypes.func,
  onMoveUp: PropTypes.func,
  choiceIconSetOptions: PropTypes.array,
}

export function QuestionBuilderCard({
  primary = false,
  question,
  questions,
  questionResponse,
  setFieldValue,
  questionIndex,
  canChangePrimaryMetric,
  arrayHelpers,
  choiceIconSetOptions,
  disableMoveUp,
  onMoveUp,
  disableMoveDown,
  onMoveDown,
  onRemove,
  removeVisibilityCondition,
  surveyToken,
  questionIdWithVisibilityRuleError,
}) {
  const { values } = useFormikContext()
  const [moveDirection, setMoveDirection] = useState('')
  const { enableMetricReorder } = useFeatureToggle()

  // Sm2: hint on slider/multi-select cards that sit at the head of the list
  // — they cannot gate to a follow-up metric question. The metric card renders
  // through the `primary` branch below, so this non-primary hint never fires on
  // the metric question itself — no metric-ness guard needed here.
  const showSliderCantGateHint =
    enableMetricReorder &&
    questionIndex === 0 &&
    (question?.type === QuestionType.SLIDER || question?.type === QuestionType.MULTI_SELECT_CHOICE)

  const { deletedVisibility = [], setContext } = useContext(surveySettingContext)

  function handleDeleteVisibility(visibility) {
    const newDeletedVisibility = deletedVisibility
    newDeletedVisibility.push(visibility)

    setContext({
      deletedVisibility: newDeletedVisibility,
    })
  }

  const {
    open: openWarningVisibilityModal,
    close: closeWarningVisibilityModal,
    isOpen: isOpenWarningVisibilityModal,
  } = useModal()
  const {
    open: openDeleteVisibilityModal,
    close: closeDeleteVisibilityModal,
    isOpen: isOpenDeleteVisibilityModal,
  } = useModal()
  const { open: openDeleteModal, close: closeDeleteModal, isOpen: isOpenDeleteModal } = useModal()

  const handleFieldChange = (name, valueGetter = semanticValueGetter) => {
    return R.compose(R.curryN(2, setFieldValue)(`questions.${questionIndex}.${name}`), valueGetter)
  }

  const handleRemove = () => {
    const questionIndiciesToRemoveVisibilityConditions = questions.reduce(
      (acc, question, index) => {
        const hasMatchingCondition = otherQuestionsVisibilityConditions.some(
          (condition) => condition.order === question.order
        )

        if (hasMatchingCondition) {
          acc.push(index)
        }
        return acc
      },
      []
    )

    questionIndiciesToRemoveVisibilityConditions.forEach((index) => {
      removeVisibilityCondition(index)
    })
    onRemove()
    closeDeleteVisibilityModal()
    closeDeleteModal()
  }

  const getQuestionsWithMatchingVisibilityCondition = (questions, sourceQuestionId) => {
    return questions
      ?.filter((question) =>
        (Array.isArray(question.visibilityCondition) ? question.visibilityCondition : []).some(
          (cond) => cond.sourceQuestionId === sourceQuestionId
        )
      )
      ?.map((question) => ({
        order: question.order,
        text: question.text,
        type: question.type,
      }))
  }

  function getQuestionIcon(type) {
    switch (type) {
      case QuestionType.COMMENT:
        return <i style={{ width: '18px' }} className="icon purple fas fa-quote-left" />
      case QuestionType.MULTIPLE_CHOICE:
        return <i style={{ width: '18px' }} className="icon green fas fa-list" />
      case QuestionType.MULTI_SELECT_CHOICE:
        return <i style={{ width: '18px' }} className="icon blue fas fa-list-check" />
      case QuestionType.SLIDER:
        return <i style={{ width: '18px' }} className="icon yellow fas fa-square-sliders" />
      case QuestionType.FILE_UPLOAD:
        return <i style={{ width: '18px' }} className="icon violet far fa-paperclip" />
    }
  }

  const otherQuestionsVisibilityConditions = getQuestionsWithMatchingVisibilityCondition(
    questions,
    question.id
  )

  function getSurveyQuestionVisibilityModalContent(moveDirection) {
    return moveDirection === 'up' ? (
      <>
        <p>This question is set to show only if an earlier question is answered a certain way.</p>
        <p>
          If you move it above that question, the visibility rule won't apply anymore—and this
          question will always be shown unless you update its logic.
        </p>
        <p>
          You can still move the question—just know the visibility condition will be removed if you
          proceed.
        </p>
      </>
    ) : (
      <>
        <p>The next question is set to show only if this question is answered a certain way.</p>
        <p>
          If you move it below the next question, the visibility rule won't apply anymore—and that
          question will always be shown unless you update its logic.
        </p>
        <p>
          You can still move the question—just know the visibility condition will be removed if you
          proceed.
        </p>
      </>
    )
  }

  const findAndRemoveVisibility = (index, direction) => {
    const gatedQuestionIndex = direction === 'up' ? index : index + 1
    const gatedConditions = questions[gatedQuestionIndex]?.visibilityCondition || []
    gatedConditions.forEach((condition) => {
      if (condition.id && condition.id !== 'temp_visibility_id') {
        handleDeleteVisibility(condition)
      }
    })
    removeVisibilityCondition(gatedQuestionIndex)
  }

  const handleMoveAnyDirection = () => {
    if (moveDirection === 'up') {
      findAndRemoveVisibility(questionIndex, 'up')
      onMoveUp()
    } else if (moveDirection === 'down') {
      findAndRemoveVisibility(questionIndex, 'down')
      onMoveDown()
    }
    closeWarningVisibilityModal()
  }

  const handleReorderUpClick = () => {
    const questionVisibilityIds =
      question.visibilityCondition?.map((condition) => {
        return condition.sourceQuestionId
      }) || []
    // Check if the current question has a visibility condition which is dependent on the previous question
    const prevQuestion = questions[questionIndex - 1]
    const prevFollowUpQuestionIds = prevQuestion.questionCondition?.conditions
      ? prevQuestion.questionCondition.conditions.flatMap((condition) =>
          [
            condition.followUpCommentQuestion?.id,
            condition.followUpFileUploadQuestion?.id,
            condition.followUpMultipleChoiceQuestion?.id,
            condition.followUpMultiSelectChoiceQuestion?.id,
          ].filter(Boolean)
        )
      : []
    const hasPrevFollowupQuestionId = prevFollowUpQuestionIds.some((id) =>
      questionVisibilityIds.includes(id)
    )

    if (questionVisibilityIds.includes(prevQuestion.id) || hasPrevFollowupQuestionId) {
      setMoveDirection('up')
      openWarningVisibilityModal()
    } else {
      onMoveUp()
    }
  }

  const handleReorderDownClick = () => {
    // Visibility condition of the next question
    const nextQuestion = questions[questionIndex + 1]
    const nextQuestionVisibilityIds =
      nextQuestion?.visibilityCondition?.map((condition) => {
        return condition.sourceQuestionId
      }) || []
    const currentFollowUpQuestionIds = question.questionCondition?.conditions
      ? question.questionCondition.conditions.flatMap((condition) =>
          [
            condition.followUpCommentQuestion?.id,
            condition.followUpFileUploadQuestion?.id,
            condition.followUpMultipleChoiceQuestion?.id,
            condition.followUpMultiSelectChoiceQuestion?.id,
          ].filter(Boolean)
        )
      : []
    const hasCurrentFollowupQuestionIdOnNextQuestion = currentFollowUpQuestionIds.some((id) =>
      nextQuestionVisibilityIds.includes(id)
    )
    // Check if the next question has a visibility condition which is dependent on the current question
    if (
      nextQuestionVisibilityIds.includes(question.id) ||
      hasCurrentFollowupQuestionIdOnNextQuestion
    ) {
      setMoveDirection('down')
      openWarningVisibilityModal()
    } else {
      onMoveDown()
    }
  }

  const QuestionBuilderCardModals = (
    <>
      <SurveyQuestionVisibilityModal
        isOpen={isOpenWarningVisibilityModal}
        header="Heads up—this move will break a visibility rule"
        content={getSurveyQuestionVisibilityModalContent(moveDirection)}
        buttonLabel="Move anyway"
        onClose={closeWarningVisibilityModal}
        onButtonClick={handleMoveAnyDirection}
      />
      <SurveyQuestionVisibilityModal
        isOpen={isOpenDeleteVisibilityModal}
        header="Heads up—this question controls other logic"
        content={
          <>
            <p>
              You're about to delete a question that's used in a visibility rule elsewhere. If you
              continue, the rule will be removed and the affected question(s) will always be shown
              unless you update their logic manually.
            </p>
            <b>This will affect:</b>
            <ul className="pl-0 ml-0 list-none">
              {otherQuestionsVisibilityConditions.map((item, index) => (
                <li key={index}>
                  <IconStyled>
                    {getQuestionIcon(item.type) || <i className="icon fas fa-question" />}
                  </IconStyled>
                  {item.text}
                </li>
              ))}
            </ul>
            <p>You can safely delete this question—just know the logic won't be preserved.</p>
          </>
        }
        buttonLabel="Delete anyway"
        onClose={closeDeleteVisibilityModal}
        onButtonClick={handleRemove}
      />
      <SurveyQuestionVisibilityModal
        isOpen={isOpenDeleteModal}
        header="Delete question?"
        content={
          <p>
            Deleting this question will permanently remove it from your survey. Any existing answers
            collected for this question will remain in your workspace.
          </p>
        }
        buttonLabel="Delete"
        onClose={closeDeleteModal}
        onButtonClick={handleRemove}
      />
    </>
  )

  const defaultIconSetId = R.path(['choiceIconSet', 'id'], question)
  if (primary) {
    return (
      <QuestionBuilderProvider
        value={{
          defaultIconSetId,
          defaultIconSet: getIconSetById(defaultIconSetId, choiceIconSetOptions),
          iconSetOptions: choiceIconSetOptions,
          setFieldValue,
          handleFieldChange,
          question,
          questionResponse,
          questionIndex,
          arrayHelpers,
        }}
      >
        <Segment.Group horizontal>
          {/* PET-1965: flag on → numbered sidebar with reorder controls only.
              Metric identity now lives in the card body's Metric field, so the
              sidebar carries no metric label and no grey background. `pt0 mt4`
              reserves the strip the absolutely-positioned ReorderButtons occupy.
              Flag off → legacy grey "Primary question" sidebar, no reorder. */}
          <Sidebar
            primary={!enableMetricReorder}
            className={enableMetricReorder ? 'shadow-none pt0 mt4' : 'shadow-none'}
          >
            {enableMetricReorder ? (
              <>
                <QuestionNumber>{questionIndex + 1}</QuestionNumber>
                <ReorderButtons>
                  <ReorderButtons.Button
                    icon="far fa-arrow-up"
                    disabled={disableMoveUp}
                    data-testid="question-reorder-up-btn"
                    onClick={handleReorderUpClick}
                  />
                  <ReorderButtons.Button
                    icon="far fa-arrow-down"
                    disabled={disableMoveDown}
                    data-testid="question-reorder-down-btn"
                    onClick={handleReorderDownClick}
                  />
                </ReorderButtons>
              </>
            ) : (
              <TooltipLabel
                label="Primary question"
                descriptionTitle="Primary question"
                descriptionContent={
                  <React.Fragment>
                    <p>
                      The first question of a survey is connected with a customer satisfaction
                      metric, such as CSAT or NPS.
                    </p>
                    <p>
                      This question and metric is used to calculate the survey’s main stats,
                      leaderboards, and drive notifications.
                    </p>
                  </React.Fragment>
                }
                learnMoreLink="https://help.simplesat.io/surveys/survey-metrics-explained"
              />
            )}
          </Sidebar>
          <Content className="shadow-none">
            <ContentWrapper>
              {/* The reorder buttons above can open the visibility-warning modal. */}
              {enableMetricReorder && QuestionBuilderCardModals}
              <PrimaryQuestionInput canChangePrimaryMetric={canChangePrimaryMetric} />
              <QuestionConditionLogic
                questionList={values.questions}
                question={question}
                surveyToken={surveyToken}
                isPrimary={primary}
                setFieldValue={setFieldValue}
                questionIndex={questionIndex}
              />
            </ContentWrapper>
          </Content>
        </Segment.Group>
      </QuestionBuilderProvider>
    )
  }
  return (
    <QuestionBuilderProvider
      value={{
        defaultIconSetId,
        defaultIconSet: getIconSetById(defaultIconSetId, choiceIconSetOptions),
        iconSetOptions: choiceIconSetOptions,
        setFieldValue,
        handleFieldChange,
        question,
        questionResponse,
        questionIndex,
        arrayHelpers,
      }}
    >
      <Segment.Group horizontal>
        <Sidebar className="flex content-between shadow-none pt0 mt4">
          {/* PET-1965: every question shows its position number in the sidebar. */}
          <QuestionNumber>{questionIndex + 1}</QuestionNumber>
          {/* Non-primary reorder renders unconditionally: legacy behaviour (flag
              off) already ships this in prod, and with enable_metric_reorder on
              reordering is likewise allowed — both paths collapse to always-on. */}
          <ReorderButtons>
            <ReorderButtons.Button
              icon="far fa-arrow-up"
              disabled={disableMoveUp}
              data-testid="question-reorder-up-btn"
              onClick={handleReorderUpClick}
            />
            <ReorderButtons.Button
              icon="far fa-arrow-down"
              disabled={disableMoveDown}
              data-testid="question-reorder-down-btn"
              onClick={handleReorderDownClick}
            />
          </ReorderButtons>
          <DeleteIcon
            className="ui icon button"
            data-testid="question-delete-btn"
            onClick={
              otherQuestionsVisibilityConditions.length
                ? openDeleteVisibilityModal
                : openDeleteModal
            }
          >
            <i className="far fa-trash-can link icon" />
          </DeleteIcon>
        </Sidebar>
        <Content className="shadow-none">
          <ContentWrapper>
            {QuestionBuilderCardModals}
            {showSliderCantGateHint && (
              <Message
                warning
                data-testid="slider-cant-gate-hint"
                content="Slider and multi-select questions cannot gate to a follow-up metric question. Add a multiple-choice non-metric question above the metric instead."
              />
            )}
            <QuestionInput />
            <QuestionConditionLogic
              questionList={values.questions}
              question={question}
              surveyToken={surveyToken}
              isPrimary={primary}
              setFieldValue={setFieldValue}
              questionIndex={questionIndex}
              questionIdWithVisibilityRuleError={questionIdWithVisibilityRuleError}
            />
          </ContentWrapper>
        </Content>
      </Segment.Group>
    </QuestionBuilderProvider>
  )
}

const DeleteIcon = styled.div`
  cursor: pointer;
  margin-top: auto !important;

  &.ui.button {
    color: #9b9b9b !important;
  }

  &.ui.button,
  &.ui.button:active,
  &.ui.button:hover {
    background-color: transparent !important;
  }
`



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/QuestionBuilderCard/index.js